import { Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';

import { Bout, PagedResponse } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { IJsonApiResponse } from '../json-api/json-api.interface';

/**
 * Service to get and save bout data.
 */
@Injectable()
export class BoutService {
    public static INCLUDE_TOP_WRESTLER_W_TEAM: string = 'topWrestler.team';
    public static INCLUDE_BOTTOM_WRESTLER_W_TEAM: string = 'bottomWrestler.team';
    public static INCLUDE_WEIGHT_CLASS: string = 'weightClass';
    public static INCLUDE_ATTRIBUTES: string = 'attributes';

    /**
     * Constructor
     */
    constructor(private http: HttpServiceHelper) {
    }

    /**
     * Get all bouts for the specified event
     */
    public getIndex(
        eventId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<PagedResponse> {
        queryParams = this.appendGetFields(queryParams);
        const apiRequestUrl: string = this.getApiUrl(eventId, null, includes, queryParams);
        return this.getIndexByUrl(apiRequestUrl);
    }

    /**
     * Get teams by a url. This is used for paging through teams since the response
     * provides the proper page urls
     */
    public getIndexByUrl(url: string): Observable<PagedResponse> {
        return this.http.get(url)
            .pipe(map((response: IJsonApiResponse) => {
                const json: any = response;

                const bouts: Bout[] = [];
                for (const bout of json.data) {
                    bouts.push((new Bout()).deserialize(bout, json.included));
                }

                return new PagedResponse(
                    json['links']['first'],
                    json['links']['last'],
                    json['links']['prev'],
                    json['links']['next'],
                    json['meta']['total'],
                    bouts,
                );
            }));
    }

    /**
     * Get a single schedule item's data
     */
    public get(
        eventId: string,
        boutId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<Bout> {
        queryParams = this.appendGetFields(queryParams);
        const apiRequestUrl: string = this.getApiUrl(eventId, boutId, includes, queryParams);

        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) => (new Bout().deserialize(response))));
    }

    /**
     * Add or update the bout
     */
    public save(bout: Bout, includes: string[] = null, queryParams: string[] = null): Observable<Bout> {
        queryParams = this.appendGetFields(queryParams);
        let response: Observable<IJsonApiResponse>;
        const boutId: string = !bout.isNew ? bout.id : null;
        const apiRequestUrl: string = this.getApiUrl(bout.eventId, boutId, includes, queryParams);

        if (!bout.isNew) {
            // Update existing Bout
            response = this.http.patch(apiRequestUrl, bout.serialize());
        } else {
            // Add a new Bout
            response = this.http.post(apiRequestUrl, bout.serialize());
        }

        return response.pipe(map((response: IJsonApiResponse) => (new Bout()).deserialize(response)));
    }

    /**
     * Delete the specified bout.
     */
    public delete(eventId: string, boutId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(eventId, boutId, null);
        return this.http.delete(apiRequestUrl).pipe(map((response: IJsonApiResponse) => true));
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(
        eventId: string,
        boutId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): string {
        let url: string = `${this.http.configService.getApiBaseUrl()}/events/${eventId}/bouts/`;

        if (boutId) {
            url += boutId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }

    private appendGetFields(queryParams: string[] = []): string[] {
        queryParams = queryParams || [];

        // Only fetch specific fields
        if (queryParams.filter((x: string) => x.indexOf('fields[bout]') !== -1).length === 0) {
            queryParams.push(`fields[bout]=${Bout.getFields().join(',')}`);
        }

        return queryParams;
    }
}
